// src/pages/Calibration/CalibrationInstrumentDetail.jsx
//
// AISC §1.14 Calibration Phase 1 — instrument unit detail with event history.

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Container, Stack, Typography, Breadcrumbs, Box, Card, CardContent,
  Grid, Button, Paper, Alert, CircularProgress,
  TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import { calibrationUnitsApi, calibrationEventsApi, daysUntil, CATEGORY_LABELS } from "../../utils/calibrationApi";
import CalibrationStatusBadge from "../../components/CalibrationStatusBadge/CalibrationStatusBadge";
import { useSelector } from "react-redux";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";

import SmartTable from "../../components/SmartTable/SmartTable";
export default function CalibrationInstrumentDetail() {
  // S-71 (2026-10-07): calibration writes are shown only with manage_calibration (Admin, Quality
  // Manager, Quality Control Inspector). The server refuses them without it (403).
  const { loginUser } = useSelector((state) => state.userSlice);
  const canManageCalibration = hasPermission(loginUser?.decodedToken?.permissions || [], PERMISSIONS.MANAGE_CALIBRATION);
  const { id } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [oosOpen, setOosOpen] = useState(false);
  const [oosReason, setOosReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, e] = await Promise.all([
        calibrationUnitsApi.get(id),
        calibrationEventsApi.listForUnit(id),
      ]);
      setUnit(u?.data?.data || null);
      setEvents(e?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleOos() {
    if (!oosReason || oosReason.trim().length < 4) {
      toast.error("Reason required (min 4 chars)");
      return;
    }
    try {
      await calibrationUnitsApi.outOfService(id, oosReason);
      toast.success("Marked out of service");
      setOosOpen(false);
      setOosReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    }
  }

  if (loading) return <Container><CircularProgress sx={{ mt: 4 }} /></Container>;
  if (!unit) return <Container><Alert severity="error" sx={{ mt: 2 }}>Instrument unit not found</Alert></Container>;

  const status = unit.effective_status || unit.status;
  const days = daysUntil(unit.next_due_date);
  const isOverdue = days != null && days < 0;

  return (
    <Container maxWidth="xl">
      <Helmet><title>{unit.serial_number} | Calibration | SFM</title></Helmet>

      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <RouterLink to="/calibration" style={{ color: "inherit" }}>Calibration</RouterLink>
        <Typography color="text.primary">{unit.serial_number}</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h4">{unit.instrument?.name || `Instrument #${unit.instrument_id}`}</Typography>
          <Typography variant="body1" color="text.secondary">Serial: <strong>{unit.serial_number}</strong> {unit.asset_tag && `· Asset Tag: ${unit.asset_tag}`}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {canManageCalibration && (
            <Button variant="contained" onClick={() => navigate(`/calibration/units/${id}/events/new`)}>
              + Record New Calibration
            </Button>
          )}
          {canManageCalibration && status !== "out_of_service" && (
            <Button variant="outlined" color="warning" onClick={() => setOosOpen(true)}>
              Take Out of Service
            </Button>
          )}
        </Stack>
      </Stack>

      {isOverdue && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>OVERDUE</strong> — Calibration was due {unit.next_due_date} ({Math.abs(days)} days ago).
          Per AISC 207-25 §2.7 and Chesakl PR-1.14, this instrument cannot be used for final acceptance until calibration is performed and recorded.
          Disposition workflow for work performed since {unit.next_due_date} is coming soon.
        </Alert>
      )}

      {status === "rejected" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>REJECTED</strong> — last calibration event failed. Cannot be used for final acceptance until passing calibration is recorded.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">STATUS</Typography><Box mt={0.5}><CalibrationStatusBadge status={status} /></Box></Grid>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">CATEGORY</Typography><Typography>{CATEGORY_LABELS[unit.instrument?.category] || unit.instrument?.category || "—"}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">FREQUENCY</Typography><Typography>{unit.frequency_days_override || unit.instrument?.default_frequency_days || "—"} days</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">EXTERNAL LAB</Typography><Typography>{unit.instrument?.requires_external_lab ? "Required" : "Not required"}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">LAST CAL</Typography><Typography>{unit.last_calibration_date || "—"}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">NEXT DUE</Typography><Typography color={isOverdue ? "error.main" : undefined}>{unit.next_due_date || "—"}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">DAYS REMAINING</Typography><Typography color={isOverdue ? "error.main" : days != null && days <= 30 ? "warning.dark" : undefined} fontWeight={isOverdue ? 600 : 400}>{days != null ? (days >= 0 ? `+${days}` : days) : "—"}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">MAKE / MODEL</Typography><Typography>{[unit.make, unit.model].filter(Boolean).join(" / ") || "—"}</Typography></Grid>
          </Grid>
          {unit.notes && <Box mt={2}><Typography variant="caption" color="text.secondary">NOTES</Typography><Typography variant="body2">{unit.notes}</Typography></Box>}
        </CardContent>
      </Card>

      <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>Calibration History ({events.length})</Typography>

      <TableContainer component={Paper}>
        <SmartTable tableId="calibration-history" lockedColumns={["Date", "Result"]} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Performed by / Lab</TableCell>
              <TableCell>Tolerance Actual</TableCell>
              <TableCell>Tolerance Limit</TableCell>
              <TableCell>CofC #</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>No calibration events recorded yet.</TableCell></TableRow>
            ) : events.map((evt) => (
              <TableRow key={evt.id}>
                <TableCell>{new Date(evt.event_date).toISOString().slice(0, 10)}</TableCell>
                <TableCell><CalibrationStatusBadge status={evt.result === "pass" ? "in_cal" : "rejected"} /></TableCell>
                <TableCell>{evt.method === "external_lab" ? "External lab" : "In-house"}</TableCell>
                <TableCell>{evt.method === "external_lab" ? evt.external_lab_name : `User #${evt.performed_by_user_id || "—"}`}</TableCell>
                <TableCell>{evt.tolerance_actual || "—"}</TableCell>
                <TableCell>{evt.tolerance_limit || "—"}</TableCell>
                <TableCell>{evt.external_lab_cofc_number || (evt.cofc_document_id ? `Doc #${evt.cofc_document_id}` : "—")}</TableCell>
                <TableCell>{evt.notes || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </SmartTable>
      </TableContainer>

      <Dialog open={oosOpen} onClose={() => setOosOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Take Instrument Out of Service</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action permanently retires the instrument from active calibration tracking. Audit_log captures the reason.
          </Typography>
          <TextField
            label="Reason (required, min 4 chars)"
            value={oosReason}
            onChange={(e) => setOosReason(e.target.value)}
            multiline rows={3}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOosOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleOos}>Take Out of Service</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
